import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const BASE_TITLE_KEY = "__base_document_title__";

/**
 * Updates document.title with an unread-message count prefix:
 *   "(3) GabelGuide" when the user has unread chats / notifications.
 *
 * Mounts once globally; reacts to realtime updates on chat_messages
 * and customer_notifications.
 */
export function useUnreadTitle() {
  const { user } = useAuth();
  const baseTitleRef = useRef<string>("");

  useEffect(() => {
    // Capture base title once
    if (!(window as any)[BASE_TITLE_KEY]) {
      (window as any)[BASE_TITLE_KEY] = document.title || "GabelGuide";
    }
    baseTitleRef.current = (window as any)[BASE_TITLE_KEY];
  }, []);

  useEffect(() => {
    if (!user) {
      document.title = baseTitleRef.current || "GabelGuide";
      return;
    }

    let cancelled = false;

    const refresh = async () => {
      try {
        const [convRes, notifRes] = await Promise.all([
          supabase
            .from("conversations")
            .select("unread_customer")
            .eq("customer_id", user.id),
          supabase
            .from("customer_notifications")
            .select("id", { count: "exact", head: true })
            .eq("user_id", user.id)
            .eq("is_read", false),
        ]);

        if (cancelled) return;

        const unreadChat = (convRes.data || []).reduce(
          (sum, c: { unread_customer: number | null }) => sum + (c.unread_customer || 0),
          0
        );
        const unreadNotif = notifRes.count || 0;
        const total = unreadChat + unreadNotif;

        const base = baseTitleRef.current || document.title.replace(/^\(\d+\)\s*/, "");
        document.title = total > 0 ? `(${total > 99 ? "99+" : total}) ${base}` : base;
      } catch {
        /* ignore */
      }
    };

    refresh();

    // Realtime: refresh whenever conversations or notifications change
    const channel = supabase
      .channel(`unread-title-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "conversations", filter: `customer_id=eq.${user.id}` },
        () => refresh()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "customer_notifications", filter: `user_id=eq.${user.id}` },
        () => refresh()
      )
      .subscribe();

    // Refresh on tab focus
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      supabase.removeChannel(channel);
    };
  }, [user]);
}
